import React from 'react';

const FourFlowProfilesDetails  = ({ createdAt,userName }) =>  {
  const deepThinker = [
    {
      title: 'DEEP THINKER',
      tagline: 'The solitary analyst who finds flow in deep, uninterrupted focus.',
      accentColor: 'border-l-[#006494]',
      headerBg: 'bg-[#006494]/10',
      titleColor: 'text-sky-700',
      rows: [
        { label: 'Behavioral tendencies', content: 'Introspective and analytical. Prefers to work alone, going deep on a single complex problem. Values mastery, precision, and the quiet satisfaction of cracking something hard. Recharges in solitude; finds noise and interruption costly.' },
        { label: 'Ideal environment', content: 'Quiet, controlled, distraction-free. A closed door, headphones, long unbroken blocks of time. Minimal context-switching and few meetings.' },
        { label: 'Preferred triggers', content: 'Deep embodiment and complete concentration; clear goals; immediate feedback from the work itself; rich, complex problems that demand full cognitive engagement (novelty of ideas over novelty of sensation).' },
        { label: 'Energy management', content: 'Internally regulated. Generates and protects their own energy; drained by excessive social demand and external stimulation.' },
        { label: 'Common pitfalls', content: 'Isolation and over-analysis. Can get stuck in planning, perfectionism, or rumination. Avoids the productive risk and social input that would sharpen the work. Vulnerable to digital interruption shattering hard-won concentration.' }
      ]
    }
  ]

  const hardCharger = [
    {
      title: 'HARD CHARGER',
      tagline: 'The bold risk-taker who finds flow on the edge of high consequence.',
      accentColor: 'border-l-[#A84B2F]',
      headerBg: 'bg-[#A84B2F]/10',
      titleColor: 'text-rose-700',
      rows: [
        { label: 'Behavioral tendencies', content: 'Action-oriented, competitive, and decisive. Drawn to challenge, intensity, and stakes. Learns by doing, moves fast, and thrives when there is something real on the line. Bored by routine and low-stakes work.' },
        { label: 'Ideal environment', content: 'High-challenge, high-stakes, fast-moving. Clear targets, real consequences, room to push limits. Autonomy to act without excessive process.' },
        { label: 'Preferred triggers', content: 'High consequences and risk; deep embodiment through physical or high-pressure action; clear goals with tight feedback loops; the challenge-skills balance pushed toward the upper edge.' },
        { label: 'Energy management', content: 'Externally fueled by challenge and adrenaline. Energized by pressure and competition; can crash when the intensity disappears.' },
        { label: 'Common pitfalls', content: 'Burnout and recklessness. Chases intensity past the point of sustainability, takes risks without recovery, and can neglect reflection. May struggle in low-stimulation remote settings that lack visible stakes.' }
      ]
    }
  ]

  const flowGoer = [
    {
      title: 'FLOW GOER',
      tagline: 'The open, adaptable explorer who finds flow through novelty and ease.',
      accentColor: 'border-l-[#20808D]',
      headerBg: 'bg-[#20808D]/10',
      titleColor: 'text-sky-700',
      rows: [
        { label: 'Behavioral tendencies', content: 'Curious, spontaneous, and adaptable. Goes with the current rather than forcing outcomes. Finds flow through play, exploration, and variety. Comfortable with ambiguity and improvisation; dislikes rigid structure.' },
        { label: 'Ideal environment', content: 'Open, flexible, and varied. Freedom to roam between ideas and tasks, low rigidity, space to follow curiosity. Novel stimuli and changing scenery.' },
        { label: 'Preferred triggers', content: 'Novelty, unpredictability, and rich environments; creativity and pattern recognition; deep embodiment through movement and play; openness over control.' },
        { label: 'Energy management', content: 'Balanced and rhythmic - flows between internal and external sources. Renewed by variety and new experiences; drained by monotony and over-control.' },
        { label: 'Common pitfalls', content: 'Lack of follow-through. Can scatter across too many interests, struggle to finish, and avoid the disciplined repetition that deepens mastery. Endless digital novelty (feeds, tabs, tools) can fragment attention.' }
      ]
    }
  ]

  const crowdPleaser = [
    {
      title: 'CROWD PLEASER',
      tagline: 'The connected collaborator who finds flow in shared, social momentum.',
      accentColor: 'border-l-[#7A39BB]',
      headerBg: 'bg-[#7A39BB]/10',
      titleColor: 'text-rose-700',
      rows: [
        { label: 'Behavioral tendencies', content: 'Social, empathetic, and energizing. Finds flow with and through other people - collaboration, performance, teaching, leading. Reads the room, lifts the group, and feeds off collective momentum. Dislikes working in isolation.' },
        { label: 'Ideal environment', content: 'Collaborative, interactive, and people-rich. Teams, audiences, real-time exchange, shared goals. Energy and feedback flowing between people.' },
        { label: 'Preferred triggers', content: 'Shared, collective flow group flow rich social interaction and communication; clear shared goals; close listening and yes-and building complete concentration on the groups task.' },
        { label: 'Energy management', content: 'Externally generated through connection. Charged by people and interaction; depleted by prolonged solitude and silence.' },
        { label: 'Common pitfalls', content: 'Dependence on others and people-pleasing. Struggles to focus or self-start alone, may over-prioritize harmony over hard truths, and can lose themselves in the group. Remote/async work can starve them of the live energy they need.' }
      ]
    }
  ]

  return (
    <>
      <div className="p-10 px-6 space-y-6 bg-white">
        <div className="px-12 space-y-6 page-break">
          <div className="flex items-center justify-between w-full pt-4 pb-2 border-b text-[#212121]/85 italic">
            <h1 className="text-lg font-semibold">Flow Profile Report</h1>
            <p className="text-lg font-semibold">{userName} | {createdAt}</p>
          </div>

          {/* Title Section */}
          <div className="mt-6 mb-8">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
              The Four Flow Profiles
            </h1>
            <p className="text-base text-gray-600 mt-1">
              Behavioral tendencies, ideal environment, preferred triggers, and common pitfalls.
            </p>
          </div>

          {/* Profile Breakdown Stack */}
          <div className="space-y-12">
            {deepThinker.map((profile, pIdx) => (
              <div key={pIdx} className="space-y-1">
                {/* Profile Header Block */}
                <div className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 border-l-4 ${profile.accentColor} ${profile.headerBg} rounded-r-sm`}>
                  <div className="sm:w-[30%]">
                    <span className="block text-sm uppercase font-bold text-sky-700 tracking-wider mb-0.5">
                      FLOW PROFILE
                    </span>
                    <span className="text-xl font-bold text-gray-900 tracking-tight">
                      {profile.title}
                    </span>
                  </div>
                  <div className="sm:w-[70%] mt-1 sm:mt-0 text-base text-gray-700 sm:pl-4">
                    {profile.tagline}
                  </div>
                </div>

                {/* Grid / Matrix Rows */}
                <div className="pt-2">
                  {profile.rows.map((row, rIdx) => (
                    <div key={rIdx} className="flex flex-col sm:flex-row items-stretch py-1 text-base gap-2 sm:gap-4">
                      <div className="w-full sm:w-[30%] bg-gray-50 text-gray-800 font-bold px-3 py-2 rounded-sm flex-shrink-0 flex items-center">{row.label}</div>
                      <div className="w-full sm:w-[70%] text-gray-600 leading-relaxed py-2 px-1">{row.content}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="p-10 px-6 space-y-6 bg-white">
        <div className="px-12 space-y-6 page-break">
          <div className="flex items-center justify-between w-full pt-4 pb-2 border-b text-[#212121]/85 italic">
            <h1 className="text-lg font-semibold">Flow Profile Report</h1>
            <p className="text-lg font-semibold">{userName} | {createdAt}</p>
          </div>

          <div className="mt-6 mb-8">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">The Four Flow Profiles (Continue)</h1>
            <p className="text-base text-gray-600 mt-1">Behavioral tendencies, ideal environment, preferred triggers, and common pitfalls.</p>
          </div>

          <div className="space-y-12">
            {hardCharger.map((profile, pIdx) => (
              <div key={pIdx} className="space-y-1">
                {/* Profile Header Block */}
                <div className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 border-l-4 ${profile.accentColor} ${profile.headerBg} rounded-r-sm`}>
                  <div className="sm:w-[30%]">
                    <span className="block text-sm uppercase font-bold text-[#A84B2F] tracking-wider mb-0.5">
                      FLOW PROFILE
                    </span>
                    <span className="text-xl font-bold text-gray-900 tracking-tight">
                      {profile.title}
                    </span>
                  </div>
                  <div className="sm:w-[70%] mt-1 sm:mt-0 text-base text-gray-700 sm:pl-4">
                    {profile.tagline}
                  </div>
                </div>

                {/* Grid / Matrix Rows */}
                <div className="pt-2">
                  {profile.rows.map((row, rIdx) => (
                    <div key={rIdx} className="flex flex-col sm:flex-row items-stretch py-1 text-base gap-2 sm:gap-4">
                      <div className="w-full sm:w-[30%] bg-gray-50 text-gray-800 font-bold px-3 py-2 rounded-sm flex-shrink-0 flex items-center">{row.label}</div>
                      <div className="w-full sm:w-[70%] text-gray-600 leading-relaxed py-2 px-1">{row.content}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="p-10 px-6 space-y-6 bg-white">
        <div className="px-12 space-y-6 page-break">
          <div className="flex items-center justify-between w-full pt-4 pb-2 border-b text-[#212121]/85 italic">
            <h1 className="text-lg font-semibold">Flow Profile Report</h1>
            <p className="text-lg font-semibold">{userName} | {createdAt}</p>
          </div>

          {/* Title Section */}
          <div className="mt-6 mb-8">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">The Four Flow Profiles (Continue)</h1>
            <p className="text-base text-gray-600 mt-1">Behavioral tendencies, ideal environment, preferred triggers, and common pitfalls.</p>
          </div>

          {/* Profile Breakdown Stack */}
          <div className="space-y-12">
            {flowGoer.map((profile, pIdx) => (
              <div key={pIdx} className="space-y-1">
                {/* Profile Header Block */}
                <div className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 border-l-4 ${profile.accentColor} ${profile.headerBg} rounded-r-sm`}>
                  <div className="sm:w-[30%]">
                    <span className="block text-sm uppercase font-bold text-[#20808D] tracking-wider mb-0.5">
                      FLOW PROFILE
                    </span>
                    <span className="text-xl font-bold text-gray-900 tracking-tight">
                      {profile.title}
                    </span>
                  </div>
                  <div className="sm:w-[70%] mt-1 sm:mt-0 text-base text-gray-700 sm:pl-4">
                    {profile.tagline}
                  </div>
                </div>

                {/* Grid / Matrix Rows */}
                <div className="pt-2">
                  {profile.rows.map((row, rIdx) => (
                    <div key={rIdx} className="flex flex-col sm:flex-row items-stretch py-1 text-base gap-2 sm:gap-4">
                      <div className="w-full sm:w-[30%] bg-gray-50 text-gray-800 font-bold px-3 py-2 rounded-sm flex-shrink-0 flex items-center">{row.label}</div>
                      <div className="w-full sm:w-[70%] text-gray-600 leading-relaxed py-2 px-1">{row.content}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="p-10 px-6 space-y-6 bg-white">
        <div className="px-12 space-y-6 page-break">
          <div className="flex items-center justify-between w-full pt-4 pb-2 border-b text-[#212121]/85 italic">
            <h1 className="text-lg font-semibold">Flow Profile Report</h1>
            <p className="text-lg font-semibold">{userName} | {createdAt}</p>
          </div>

          <div className="mt-6 mb-8">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">The Four Flow Profiles (Continue)</h1>
            <p className="text-base text-gray-600 mt-1">Behavioral tendencies, ideal environment, preferred triggers, and common pitfalls.</p>
          </div>

          <div className="space-y-12">
            {crowdPleaser.map((profile, pIdx) => (
              <div key={pIdx} className="space-y-1">
                {/* Profile Header Block */}
                <div className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 border-l-4 ${profile.accentColor} ${profile.headerBg} rounded-r-sm`}>
                  <div className="sm:w-[30%]">
                    <span className="block text-sm uppercase font-bold text-[#7A39BB] tracking-wider mb-0.5">
                      FLOW PROFILE
                    </span>
                    <span className="text-xl font-bold text-gray-900 tracking-tight">
                      {profile.title}
                    </span>
                  </div>
                  <div className="sm:w-[70%] mt-1 sm:mt-0 text-base text-gray-700 sm:pl-4">
                    {profile.tagline}
                  </div>
                </div>

                {/* Grid / Matrix Rows */}
                <div className="pt-2">
                  {profile.rows.map((row, rIdx) => (
                    <div key={rIdx} className="flex flex-col sm:flex-row items-stretch py-1 text-base gap-2 sm:gap-4">
                      <div className="w-full sm:w-[30%] bg-gray-50 text-gray-800 font-bold px-3 py-2 rounded-sm flex-shrink-0 flex items-center">{row.label}</div>
                      <div className="w-full sm:w-[70%] text-gray-600 leading-relaxed py-2 px-1">{row.content}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default FourFlowProfilesDetails;